import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FileText, Trash2, ExternalLink, Tag, FolderTree } from 'lucide-react'
import {
  PageHeader, Card, CardHeader, CardBody, Button, Badge, FormField, FormSelect,
  FormTextarea, LoadingSpinner, ErrorMessage, SuccessMessage, ConfirmationModal
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'

const categoryOptions = [
  { value: 'RulesAndRegulations', label: 'Rules & Regulations' },
  { value: 'StrataDocuments', label: 'Strata Documents' },
  { value: 'Financial', label: 'Financial' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'MeetingMinutes', label: 'Meeting Minutes' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'General', label: 'General' },
]

const statusBadgeVariant = {
  Uploaded: 'default',
  Processing: 'warning',
  Indexed: 'success',
  Failed: 'danger',
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function KnowledgeDocumentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editData, setEditData] = useState({ title: '', description: '', category: '', tags: '' })

  useEffect(() => {
    loadDocument()
  }, [id])

  const loadDocument = async () => {
    try {
      setLoading(true)
      const response = await knowledgeBaseApi.getDocument(id)
      const doc = response.data
      setDocument(doc)
      setEditData({
        title: doc.title || '',
        description: doc.description || '',
        category: doc.category || 'General',
        tags: (doc.tags || []).join(', ')
      })
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const tags = editData.tags ? editData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      await knowledgeBaseApi.updateDocument(id, {
        title: editData.title || null,
        description: editData.description || null,
        category: editData.category || null,
        tags
      })
      setSuccess('Document updated successfully')
      loadDocument()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await knowledgeBaseApi.deleteDocument(id)
      navigate('/knowledge-base/documents')
    } catch (err) {
      setError(extractErrorMessage(err))
      setDeleting(false)
    }
  }

  if (loading) return <LoadingSpinner />
  if (!document) return <ErrorMessage message="Document not found" />

  return (
    <div className="space-y-6">
      <PageHeader
        title={document.title || document.fileName}
        subtitle={`Uploaded ${new Date(document.createdAt).toLocaleDateString()}`}
        backLink={{ label: 'Back to Documents', href: '/knowledge-base/documents' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Document Metadata" />
            <CardBody className="space-y-4">
              <FormField label="Title" value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))} />
              <FormTextarea label="Description" value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))} rows={3} />
              <FormSelect label="Category" value={editData.category}
                onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                options={categoryOptions} />
              <FormField label="Tags (comma-separated)" value={editData.tags}
                onChange={(e) => setEditData(prev => ({ ...prev, tags: e.target.value }))} />
              <div className="flex gap-3 pt-2">
                <Button variant="primary" onClick={handleSave} loading={saving}>Save Changes</Button>
              </div>
            </CardBody>
          </Card>

          {/* Topics */}
          {document.topics?.length > 0 && (
            <Card>
              <CardHeader title="Assigned Topics" />
              <CardBody>
                <div className="flex flex-wrap gap-2">
                  {document.topics.map(topic => (
                    <Badge key={topic.id} variant="info">
                      <FolderTree size={12} className="mr-1" /> {topic.name}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right: File Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="File Info" />
            <CardBody className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <FileText size={24} className="text-primary-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{document.fileName}</p>
                  <p className="text-xs text-slate-500">{document.contentType}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Size</span>
                  <span className="text-slate-900">{formatFileSize(document.fileSize)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <Badge variant={statusBadgeVariant[document.status] || 'default'}>{document.status}</Badge>
                </div>
                {document.aiConfidence != null && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">AI Confidence</span>
                    <span className="text-slate-900">{(document.aiConfidence * 100).toFixed(0)}%</span>
                  </div>
                )}
                {document.lastSyncedAt && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last Synced</span>
                    <span className="text-slate-900">{new Date(document.lastSyncedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              {document.fileUrl && (
                <a href={document.fileUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mt-3">
                  <ExternalLink size={14} /> View File
                </a>
              )}
              {document.ingestionError && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Ingestion Error</p>
                  <p className="text-xs text-red-600">{document.ingestionError}</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Button variant="danger" icon={<Trash2 size={16} />} fullWidth
                onClick={() => setShowDeleteModal(true)}>
                Delete Document
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Document"
        message="Are you sure you want to delete this document? This will remove it from S3 and the knowledge base."
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
    </div>
  )
}
